import { TRESPONSE_OPL_LIST, TRESPONSE_ORDER_LIST } from '@monorepo/types';
import { ModifiersZTableModel } from '../../table.modifier';
import { THeaderCells, TTableCellType, TTableOptions, TTableTreeModel, TTextCell } from '../../table.type';

const createHeaderRow = (headerInfo: TRESPONSE_ORDER_LIST['field_info']): TTableOptions['headerConfig'] => {
  const headerRow = [
    ...headerInfo.map((hInfo) => {
      return {
        type: 'TEXT_CELL' as const,
        text: hInfo['display_name'],
        maxWidthInPx: 200,
      };
    }),
    {
      type: 'TEXT_CELL' as const,
      text: '',
      stickyType: 'RIGHT' as const,
    },
  ];
  return headerRow;
};

const getRows = (headerInfo: TRESPONSE_ORDER_LIST['field_info'], rowItem: TRESPONSE_ORDER_LIST['orders'][number]) => {
  const rows = [
    ...headerInfo.map((hInfo) => {
      return hInfo.key === 'user_otl_id'
        ? {
            type: 'TEXT_CELL' as const,
            text: rowItem['fields']['user_otl_id'][0],
            href: `/orders/details/${rowItem['id']}`,
          }
        : {
            type: 'TEXT_LIST_CELL' as const,
            text: rowItem['fields'][hInfo.key],
          };
    }),
    {
      type: 'ACTION_LIST_CELL' as const,
      title: 'Actions To be Taken',
      actions: rowItem['actions'].map((act) => {
        return {
          displayText: act.displayString,
          type: act.action,
          icon: act.completionStatus === true ? ('complete' as const) : ('incomplete' as const),
        };
      }),
    },
  ];

  return rows;
};

export const ORDER_LIST_PLANI8_TABLE_MODEL = {
  createModel: (headerInfo: TRESPONSE_ORDER_LIST['field_info'], rowInfo: TRESPONSE_ORDER_LIST['orders']) => {
    return ModifiersZTableModel.createTableModel(
      rowInfo,
      {
        select: {
          position: 'START',
          isMulti: true,
          size: 'DEFAULT',
          stickyType: 'LEFT',
        },
        edit: {
          hasAddRow: false,
          hasDeleteRow: true,
          stickyType: 'RIGHT',
        },
        headerConfig: createHeaderRow(headerInfo),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getRows(headerInfo, item),
        };
      },
    );
  },
};

const createHeaderRowLLModal = (headerInfo: TRESPONSE_ORDER_LIST['field_info']): TTableOptions['headerConfig'] => {
  const headerRow = [
    ...headerInfo.map((hInfo) => {
      return {
        type: 'TEXT_CELL' as const,
        text: hInfo['display_name'],
      };
    }),
  ];
  return headerRow;
};

const getRowsLLModal = (
  headerInfo: TRESPONSE_ORDER_LIST['field_info'],
  rowItem: TRESPONSE_ORDER_LIST['orders'][number],
) => {
  const rows = [
    ...headerInfo.map((hInfo) => {
      return hInfo.key === 'user_otl_id'
        ? {
            type: 'TEXT_CELL' as const,
            text: rowItem['fields']['user_otl_id'][0],
            hrefActionCB: rowItem['fields']['user_otl_id'][0] as string,
          }
        : {
            type: 'TEXT_LIST_CELL' as const,
            text: rowItem['fields'][hInfo.key],
          };
    }),
  ];

  return rows;
};

export const LL_ORDER_LIST_PLANI8_TABLE_MODEL = {
  createModel: (headerInfo: TRESPONSE_ORDER_LIST['field_info'], rowInfo: TRESPONSE_ORDER_LIST['orders']) => {
    return ModifiersZTableModel.createTableModel(
      rowInfo,
      {
        headerConfig: createHeaderRowLLModal(headerInfo),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getRowsLLModal(headerInfo, item),
        };
      },
    );
  },
};

const createPlanningTableHeaderRow = (
  headerInfo: TRESPONSE_OPL_LIST['header_info'],
): THeaderCells<TOplListTableOrderPlanningOrderInfo>[] => {
  return [
    ...headerInfo.map(
      (hInfo): TTextCell => ({
        type: 'TEXT_CELL',
        text: hInfo.display_name,
      }),
    ),
    {
      type: 'TEXT_CELL',
      text: 'Load Qty.',
      liftedInput: {
        input: {
          type: 'INPUT',
          subType: 'number',
          min: 1,
          setValue(value) {
            return value.userLoadQty ?? Number(value.order.max_load_qty);
          },
        },
        metadata: {
          keyName: 'load_qty',
          isRequired: true,
        },
      },
    },
    {
      type: 'TEXT_CELL',
      text: '',
      widthInPx: 50,
      align: 'center',
    },
  ];
};

const getPlanningTableRows = (
  headerInfo: TRESPONSE_OPL_LIST['header_info'],
  rowItem: TOplListTableOrderPlanningOrderInfo,
): TTableCellType[] => {
  return [
    ...headerInfo.map((hInfo) => ({
      type: 'TEXT_CELL' as const,
      text: rowItem.order[hInfo.key],
    })),
    {
      type: 'TEXT_CELL',
      text: rowItem.userLoadQty ?? rowItem.order.max_load_qty,
    },
    rowItem.opl_closed
      ? {
          type: 'ACTION_CELL' as const,
          actionType: 'INFO' as const,
          customIconPath: 'assets/icon/common/close-opl-success.svg',
          actionNotAllowed: true,
          align: 'center' as const,
          asIcon: true,
          showLargeIcon: false,
        }
      : { type: 'TEXT_CELL' as const, text: '' },
  ];
};

export type TOplListTableOrderPlanningOrderInfo = {
  userLoadQty?: number;
  isSelected?: boolean;
} & TRESPONSE_OPL_LIST['orders'][number];

export const LL_OPL_LIST_PLANI8_TABLE_MODEL = {
  createModel: (
    headerInfo: TRESPONSE_OPL_LIST['header_info'],
    rowInfo: TOplListTableOrderPlanningOrderInfo[],
    selectedOtlId?: TRESPONSE_OPL_LIST['orders'][number]['otl_id'],
  ): TTableTreeModel<TOplListTableOrderPlanningOrderInfo> => {
    return ModifiersZTableModel.createTableModel(
      rowInfo,
      {
        padding: 24,
        select: {
          position: 'START',
          isMulti: true,
          size: 'DEFAULT',
          stickyType: 'LEFT',
        },
        edit: {
          hasAddRow: false,
          isEditOnly: false,
        },
        headerConfig: createPlanningTableHeaderRow(headerInfo),
      },
      (item) => {
        const isDifferentOtlId = selectedOtlId && item.otl_id !== selectedOtlId;
        return {
          type: 'ROW' as const,
          rowSelectionInfo: {
            isSelected: !!item.isSelected,
            isDisabled: item.status === 'COMPLETELY_PLANNED' || !!item.opl_closed || !!isDifferentOtlId,
            disabledReason:
              item.status === 'COMPLETELY_PLANNED'
                ? 'Order already planned completely'
                : item.opl_closed
                ? 'OPL is closed'
                : isDifferentOtlId
                ? 'Cannot plan multiple OTLs together'
                : undefined,
          },
          rowItem: item,
          cells: getPlanningTableRows(headerInfo, item),
        };
      },
    );
  },
};
